<template>
	<div class="schedule-matrix">
		<div v-if="teacherStore.selected.length <= 0" class="empty-state">
			<figure>
				<IconScheduleMatrixEmptyState />
			</figure>
			<span>
				<div class="title" v-t="'message.ScheduleMatrix.no_teachers_title'"></div>
				<p v-t="'message.ScheduleMatrix.no_teachers_message'"></p>
			</span>
			<v-btn
				@click="emit('toggle-drawer', 'teachers')"
				v-t="'message.ScheduleMatrix.choose_teachers_button'"
			>
			</v-btn>
		</div>
		<Draggable
			v-else
			class="draggable"
			id="matrix"
			data-metalines="3"
			v-model="teacherStore.list"
			handle=".handle"
			filter=".col-times"
			draggable=".col-teacher"
			flow="horizontal"
			:class="{ active: isMoving }"
			ghostClass="sortable-ghost"
			chosenClass="sortable-chosen"
			dragClass="sortable-drag"
			animation="150"
			forceFallback="true"
			fallbackClass="sortable-fallback"
			fallbackOnBody="true"
		>
			<div class="col-times" id="times">
				<header>
					<button
						id="collapse"
						class="btn-toggle collapse"
						@click="
							[(allColumnsCollapsed = !allColumnsCollapsed), $event.currentTarget.blur()]
						"
					>
						<i
							class="fs-5 text-dark bi bi-arrows-collapse"
							style="rotate: 90deg"
							v-if="!allColumnsCollapsed"
						></i>
						<i
							class="fs-5 text-dark bi bi-arrows-expand"
							style="rotate: 90deg"
							v-else
						></i>
					</button>
				</header>
				<section>
					<time
						v-for="slot in makeTimes()"
						:key="slot.time"
						:style="{ height: [getTimeIndicatorHeight()] + 'px' }"
						>{{ slot.time }}</time
					>
					<!-- This should show where the appointments are from the agenda -->
					<span
						class="inuse"
						v-for="(date, i) in activeDates"
						:key="i"
						:style="{ top: [date.top] + 'px', height: [date.height] + 'px' }"
					></span>
				</section>
			</div>

			<div
				v-for="column in teacherStore.selected"
				class="col-teacher"
				:key="column.value.uid"
				:data-collapse="allColumnsCollapsed"
				data-cy="matrix-teacher-item"
			>
				<header>
					<div class="handle">
						<IconHandle />
					</div>
					<div class="header-teacher">
						<span v-if="column.value.teacher_id">
							<span class="header-fname">{{ column.value.teacher_fname + " " }}</span>
							<span class="header-lname">{{ column.value.teacher_lname }}</span>
						</span>
						<span v-else>
							<span class="header-lname">{{ column.value.name }}</span>
						</span>
					</div>
					<div
						class="header-meta header-student"
						v-for="student in column.value.students"
						:key="student.uid"
					>
						{{ student.child_fname }} {{ student.child_lname }}
					</div>
					<div class="header-meta header-description">
						{{ column.value.description }}
					</div>
					<div class="header-meta header-location">
						{{ getRoom(column.value) ? getRoom(column.value) : "TBD" }}
					</div>
				</header>
				<section>
					<div
						class="placeholder w-100 h-100"
						v-if="!column.value.timeslots"
						:class="{ 'd-none': column.value.timeslots }"
						data-cy="matrix-placeholder"
					></div>
					<div class="gridlines" :class="{ 'd-none': !column.value.timeslots }">
						<span
							v-for="slot in makeTimes()"
							:style="{ height: [getTimeIndicatorHeight()] + 'px' }"
							:key="slot.start_time"
						></span>
					</div>
					<div
						class="timeslot"
						v-for="(slot, index) in column.value.timeslots"
						:data-loading="slot.loading"
						:key="index"
						:style="{
							top: [getPixelTop(slot.timeslot)] + 'px',
							height: [getPixelHeight(slot.duration)] + 'px',
							visibility: [getVisibility(slot.type)],
						}"
						:data-top="getPixelTop(slot.timeslot)"
						:data-start="slot.start_time.format('YYYY-MM-DD H:mm:ss')"
						:data-end="slot.end_time.format('YYYY-MM-DD H:mm:ss')"
						:data-duration="slot.duration"
						:data-meeting-type="slot.type"
						:data-id="slot.id"
						data-cy="matrix-timeslot"
						:data-timeslot="dayjs.utc(slot.timeslot).format('HH:mm')"
						v-bind="{
							['data-' + getVisibility(slot.type)]: '',
						}"
					>
						<input
							:data-timeslot-disabled="slot.disabled || undefined"
							:ref="
								(el) => {
									if (el !== null) el.checked = isChecked(slot);
								}
							"
							type="checkbox"
							class="btn-check"
							autocomplete="off"
							:id="slot.timeslot + '-' + column.value.uid"
							:column="column.value.uid"
							:disabled="slot.disabled"
							:checked="slot.checked"
							@change="set(column, slot)"
						/>
						<label
							class="btn"
							:data-type="slot.type"
							:class="['btn-timeslot-' + slot.type]"
							:title="timeslotTitle(slot)"
							:for="slot.timeslot + '-' + column.value.uid"
							@click="slot.loading = true"
						>
							{{ slot.start_time.format("h:mm") }}
						</label>
					</div>
				</section>
			</div>
		</Draggable>
	</div>

	<div
		class="modal fade"
		tabindex="-1"
		aria-labelledby=""
		aria-hidden="true"
		:class="{ 'd-block show': teacherStore.show_drawer }"
		data-cy="matrix-select-student"
	>
		<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-sm">
			<div class="modal-content">
				<div class="modal-body vstack gap-3">
					<h5 class="text-center text-muted">
						<span v-t="'message.ScheduleMatrix.choose_student'"></span>
					</h5>
					<template v-for="student in students" :key="student.uid">
						<input
							type="radio"
							class="btn-check"
							name="student-options"
							id="student-option"
							autocomplete="off"
						/>
						<label
							class="btn btn-secondary"
							for="student-option"
							@click="reserve(current_teacher, current_timeslot, student.uid)"
							data-cy="select-meeting-student-item"
							>{{ student.child_fname }} {{ student.child_lname }}</label
						>
					</template>
				</div>
				<div class="modal-footer">
					<button
						class="btn w-100 text-primary"
						data-bs-dismiss="modal"
						aria-label="Close"
						@click="CloseStudentPicker(current_timeslot)"
					>
						<span v-t="'message.ScheduleMatrix.cancel'"></span>
					</button>
				</div>
			</div>
		</div>
	</div>
	<div class="offcanvas-backdrop fade show" v-if="teacherStore.show_drawer"></div>
</template>
<script setup>
import { ref, computed } from "vue";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/esm/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/esm/plugin/isSameOrBefore";
import IconHandle from "@/assets/icons/handle.svg";
import IconScheduleMatrixEmptyState from "@/assets/icons/schedule-matrix-empty-state.svg";
import { useMatrixStore } from "@/stores/parent/matrix";
import { useTeacherStore } from "@/stores/parent/teacher";
import { useConferenceStore } from "@/stores/parent/conference";
import { useAgendaStore } from "@/stores/parent/agenda";
import { useSchoolStore } from "@/stores/school";
import { useAuthenticationStore } from "@/stores/authentication";
import { useAlertsStore } from "@/stores/alerts";
import utc from "dayjs/esm/plugin/utc";

dayjs.extend(utc);

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
const agendaStore = useAgendaStore();
const matrixStore = useMatrixStore();
const teacherStore = useTeacherStore();
const schoolStore = useSchoolStore();
const conferenceStore = useConferenceStore();
const authenticationStore = useAuthenticationStore();
const alertStore = useAlertsStore();
const allColumnsCollapsed = ref(false);
const isMoving = ref(false);

const students = ref([]);
const current_teacher = ref(null);
const current_timeslot = ref(null);
const reserving = [];

const emit = defineEmits(["toggle-drawer"]);

function isChecked(slot) {
	if (slot.checked == "checked" || slot.checked === true || slot.type == "reserved") {
		return true;
	} else {
		return false;
	}
}

function CloseStudentPicker(timeslot) {
	timeslot.type = "available";
	timeslot.checked = false;
	timeslot.disabled = false;
	timeslot.id = null;
	timeslot.loading = false;
	teacherStore.show_drawer = false;
}

function timeslotTitle(slot) {
	if (slot.type == "available")
		return (
			"Reserve " +
			slot.start_time.format("h:mm A") +
			" - " +
			slot.end_time.format("h:mm A")
		);
	else return "Cancel " + slot.start_time.format("h:mm A");
}

const isConflict = (a1, a2, b1, b2) => {
	/*
                1)  a1 < b2 & a2 > b2 = Meeting A starts during Meeting B and finishes after Meeting B
                2)  a1 < b1 & a2 > b1 = Meeting A starts before Meeting B and finishes during Meeting B
                3)  a1 < b2 & a2 < b1 = Meeting A starts and finishes during Meeting B
                4)  a1 <= b1 & a2 >= b2 = Meeting A starts before or at the same time as Meeting B and finishes at the same time or after Meeting B
            */
	if (
		(a1.isBefore(b2) && a2.isAfter(b2)) ||
		(a1.isBefore(b1) && a2.isAfter(b1)) ||
		(a1.isBefore(b2) && a2.isAfter(b1)) ||
		a2.isSame(b1) ||
		b2.isSame(a1)
	) {
		return true;
	} else {
		return false;
	}
};

const activeDates = computed(() => {
	const res = [];
	const dayStart = dayjs.utc(conferenceStore.selectedDay.start);
	const dayEnd = dayjs.utc(conferenceStore.selectedDay.end);
	[...agendaStore.agenda]
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
		.forEach((agenda) => {
			const start = dayjs.utc(agenda.start);
			const end = dayjs.utc(agenda.end);

			// loop through agenda and join all
			const overlapped = res.find((t) => isConflict(start, end, t.start, t.end));
			if (overlapped) {
				if (start.isBefore(overlapped.start) || end.isSame(overlapped.start)) {
					overlapped.start = start;
				}
				if (end.isAfter(overlapped.end) || start.isSame(overlapped.end)) {
					overlapped.end = end;
				}
			} else {
				if (
					(start.isAfter(dayStart) || start.isSame(dayStart)) &&
					(end.isBefore(dayEnd) || end.isSame(dayEnd))
				) {
					res.push({
						end,
						start,
					});
				}
			}
		});

	const data = res.map((item) => ({
		height: getPixelHeight(item.end.diff(item.start, "minutes")),
		top: getPixelTop(item.start),
	}));
	return data;
});

function getPixelHeight(duration) {
	return (40 / matrixStore.smallestTimeInterval) * duration;
}
function getPixelTop(timeslot) {
	const dayStart = dayjs.utc(conferenceStore.selectedDay.start);
	const minutes = timeslot.diff(dayStart, "minute");
	return (40 / matrixStore.smallestTimeInterval) * minutes;
}
function getVisibility(type) {
	if (type == "unavailable") {
		return "hidden";
	} else {
		return "visible";
	}
}
async function reserve(teacher_team, timeslot, student) {
	const identifier = JSON.stringify({ teacher_team, timeslot, student });
	if (reserving.includes(identifier)) return;
	reserving.push(identifier);
	let appointmentType, appointment;
	if (teacher_team.value.teacher_id) {
		appointmentType = "teacher";
		appointment = {
			start: timeslot.start_time,
			end: timeslot.end_time,
			teacher_uid: teacher_team.value.uid,
		};
	} else {
		appointmentType = "team";
		appointment = {
			start: timeslot.start_time,
			end: timeslot.end_time,
			team_uid: teacher_team.value.uid,
		};
	}
	// teacherStore.selected.forEach((t) => {
	// 	teacherStore.disableTimeslots(t.value, conferenceStore.selectedDay, appointment);
	// });
	appointment = await agendaStore.reserveMeeting(
		appointmentType,
		student,
		teacher_team.value.uid,
		conferenceStore.selectedDay.conference_id,
		conferenceStore.selectedDay.conference_days_id,
		timeslot.start_time
	);
	if (appointment?.pta_id != undefined) {
		appointment.new_meeting = true;
		appointment.name = teacher_team.value.name;
		// appointment.child_name = student.child_name;
		agendaStore.agenda.push(appointment);
		teacherStore.selected.forEach((t) => {
			teacherStore.disableTimeslots(t.value, conferenceStore.selectedDay, appointment);
		});
		timeslot.checked = "checked";
		timeslot.type = "reserved";
		timeslot.disabled = false;
		timeslot.id = appointment.pta_id;
	} else {
		alertStore.addAlert("error", appointment.message);
		timeslot.type = "available";
		timeslot.checked = false;
		timeslot.disabled = false;
	}
	timeslot.loading = false;
	teacherStore.show_drawer = false;
	students.value = [];
	current_teacher.value = null;
	current_timeslot.value = null;
	reserving.splice(reserving.indexOf(identifier), 1);

	// await agendaStore.fetchAgenda();
}
async function cancel(timeslot) {
	const status = await agendaStore.cancelMeeting(timeslot.id);
	if (status == true) {
		timeslot.type = "available";
		timeslot.checked = false;
		timeslot.disabled = false;
		let index = agendaStore.getMeetingIndexById(timeslot.id);
		timeslot.id = null;
		agendaStore.agenda.splice(index, 1);
		let booked_meetings = [];
		agendaStore.agenda
			.filter(
				(meeting) =>
					meeting.conference_days_id == conferenceStore.selectedDay.conference_days_id
			)
			.forEach((meeting) => {
				// Collect all the teachers that have appointments for this conference day so that we can skip them
				if (
					meeting.teacher_uid
						? booked_meetings.push(meeting.teacher_uid)
						: booked_meetings.push(meeting.team_uid)
				);
			});
		teacherStore.selected.forEach((t) => {
			// if (!booked_meetings.includes(t.value.uid)) {
			teacherStore.clearDisabledTimeslots(t.value);
			agendaStore.agenda
				.filter(
					(meeting) =>
						meeting.conference_days_id == conferenceStore.selectedDay.conference_days_id
				)
				.forEach((meeting) => {
					teacherStore.disableTimeslots(t.value, conferenceStore.selectedDay, meeting);
				});
			// }
		});
	} else {
		alertStore.addAlert(
			"error",
			dayjs.utc(timeslot.timeslot).format("h:MM a") + " Appointment is already cancelled."
		);
		timeslot.type = "available";
		timeslot.checked = false;
		timeslot.disabled = false;
	}
	timeslot.loading = false;
	// Removed the fetchAgenda call here since it was using resources for no reason.
}
function set(teacher_team, timeslot) {
	//Find the student for this appointment.
	if (timeslot.type == "available") {
		if (schoolStore.school.integration == "none") {
			students.value = authenticationStore.me.students;
		} else {
			// If teacher has associated students, use those; otherwise use all parent's children
			students.value = teacher_team.value.students?.length > 0 
				? teacher_team.value.students 
				: authenticationStore.me.students;
		}
		if (students.value?.length > 1) {
			if (
				students.value.length -
					teacher_team.value.timeslots.filter((timeslot) => {
						return timeslot.type == "reserved";
					}).length ==
				1
			) {
				students.value.forEach((student) => {
					if (
						agendaStore.agenda.some((meeting) => {
							return (
								meeting.children_uid == student.uid &&
								meeting.conference_days_id ==
									conferenceStore.selectedDay.conference_days_id &&
								(meeting.teacher_uid == teacher_team.value.uid ||
									meeting.team_uid == teacher_team.value.uid)
							);
						})
					) {
						//this student is already booked
						console.log("Not making appointment for: " + student.uid);
					} else {
						// we found the last student
						return reserve(teacher_team, timeslot, student.uid);
					}
				});
			} else {
				var remaining_students = [];
				students.value.forEach((student) => {
					if (
						agendaStore.agenda.some((meeting) => {
							return (
								meeting.children_uid == student.uid &&
								meeting.conference_days_id ==
									conferenceStore.selectedDay.conference_days_id &&
								(meeting.teacher_uid == teacher_team.value.uid ||
									meeting.team_uid == teacher_team.value.uid)
							);
						})
					) {
						//this student is already booked
						console.log("Not making appointment for: " + student.uid);
					} else {
						// this student doesn't have an appointment yet.
						remaining_students.push(student);
					}
				});
				students.value = remaining_students;
				// if double booking is enabled, then reset the students array to the original based on integration type
				if (schoolStore.school.double_bookings) {
					if (schoolStore.school.integration == "none") {
						students.value = authenticationStore.me.students;
					} else {
						// If teacher has associated students, use those; otherwise use all parent's children
						students.value = teacher_team.value.students?.length > 0 
							? teacher_team.value.students 
							: authenticationStore.me.students;
					}
				}
				current_teacher.value = teacher_team;
				current_timeslot.value = timeslot;
				teacherStore.show_drawer = true;

				return false;
			}
		} else {
			console.log(
				"Only 1 student with this teacher.  Using that student for this appointment"
			);
			return reserve(teacher_team, timeslot, students.value?.[0].uid);
		}
	} else if (timeslot.type == "reserved" && timeslot.id != null) {
		// cancelling the appointment and refreshing all timeslot checkboxes.
		return cancel(timeslot);
	} else {
		console.log("what do i do now? " + timeslot.type);
	}
	return true;
}

function getTimeIndicatorHeight() {
	let time_interval = 15;
	var heightPx = 40;
	if (matrixStore.smallestTimeInterval != null) {
		// the 15 minute interval is only used for the pixel per minute calculation if it is smaller than the smallest time interval
		// if the smallest interval is less than 15, then use the smallest interval to calculate the pixel per minute.
		if (time_interval > matrixStore.smallestTimeInterval) {
			time_interval = matrixStore.smallestTimeInterval;
		}
		heightPx = (40 / time_interval) * 15;
	}
	return heightPx;
}

function makeTimes() {
	const startTime = conferenceStore.selectedDay.start;
	const endTime = conferenceStore.selectedDay.end;

	const time_interval = 15;
	var x = dayjs.utc(startTime);
	var y = dayjs.utc(endTime);
	var diff = y.diff(x, "minute", true) / time_interval;
	var timeslots = [];
	var heightPx = 40;
	if (matrixStore.smallestTimeInterval != null) {
		var maxTimeslots = y.diff(x, "minute", true) / matrixStore.smallestTimeInterval;
		heightPx = (40 * maxTimeslots) / diff + (matrixStore.smallestTimeInterval - 15);
	}
	for (var i = 0; i < diff; i++) {
		timeslots.push({
			time: x.format("h:mm A"),
			start_time: x.toString(),
			end_time: x.add(time_interval, "minute").toString(),
			duration: matrixStore.smallestTimeInterval,
			height: heightPx,
		});
		x = x.add(time_interval, "minute");
	}
	return timeslots;
}
// eslint-disable-next-line
function removeColumn(teacher) {
	const index = teacherStore.getSelectedIndexById(teacher.value.uid);
	const selectedTeacher = teacherStore.getById(teacher.value.uid);
	selectedTeacher.checked = false;
	teacherStore.selected[index].value.checked = false;
	teacherStore.selected.splice(index, 1);
	if (teacherStore.count == 0) {
		matrixStore.activeTab = "teachers";
	}
}

function getRoom(teacher) {
	const days = teacher.conferencedays.filter(
		(day) => day.conference_days_id == conferenceStore.selectedDay.conference_days_id
	);
	return days[0]?.room;
}
</script>
